import { del, get, patch, post, put } from '@aiptyg/api-client';
import type {
  AdminUserDetailDTO,
  AdminWalletAdjustmentParams,
  AdminWalletAdjustmentResponseDTO,
  AdminUserListResponseDTO,
  AdminUserStatusUpdateResponseDTO,
  AdminUserUpdateParams,
  AdminUserUpdateResponseDTO,
  AdminUserReleaseAppleVerificationResponseDTO,
  AdminWalletTransactionListResponseDTO,
  AdminWalletTransactionType,
  BooleanFilterValue,
} from '../types/users.types';

export interface AdminUserListParams {
  marketId?: string;
  page: number;
  pageSize: number;
  keyword: string;
  role: string;
  status: string;
  createdFrom: string;
  createdTo: string;
  hasInviter: BooleanFilterValue;
  appleVerificationStatus: string;
  riskTagged: BooleanFilterValue;
}

export function listAdminUsers(params: AdminUserListParams): Promise<AdminUserListResponseDTO> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('page_size', String(params.pageSize));
  if (params.marketId) qs.set('market_id', params.marketId);
  if (params.keyword) qs.set('keyword', params.keyword);
  if (params.role) qs.set('role', params.role);
  if (params.status) qs.set('status', params.status);
  if (params.createdFrom) qs.set('created_from', params.createdFrom);
  if (params.createdTo) qs.set('created_to', params.createdTo);
  if (params.hasInviter) qs.set('has_inviter', params.hasInviter);
  if (params.appleVerificationStatus) qs.set('apple_verification_status', params.appleVerificationStatus);
  if (params.riskTagged) qs.set('risk_tagged', params.riskTagged);
  return get<AdminUserListResponseDTO>(`/v1/admin/users?${qs.toString()}`);
}

export function getAdminUserDetail(userId: number): Promise<AdminUserDetailDTO> {
  return get<AdminUserDetailDTO>(`/v1/admin/users/${userId}`);
}

export function updateAdminUserStatus(
  userId: number,
  payload: { status: string; reason: string },
): Promise<AdminUserStatusUpdateResponseDTO> {
  return patch<AdminUserStatusUpdateResponseDTO>(`/v1/admin/users/${userId}/status`, payload);
}

export function updateAdminUser(
  userId: number,
  payload: AdminUserUpdateParams,
): Promise<AdminUserUpdateResponseDTO> {
  return put<AdminUserUpdateResponseDTO>(`/v1/admin/users/${userId}`, {
    email: payload.email,
    nickname: payload.nickname,
    password: payload.password ?? '',
    status: payload.status,
  });
}

export function releaseAdminUserAppleVerification(
  userId: number,
): Promise<AdminUserReleaseAppleVerificationResponseDTO> {
  return del<AdminUserReleaseAppleVerificationResponseDTO>(`/v1/admin/users/${userId}/apple-verification`);
}

export function adjustAdminUserWallet(
  userId: number,
  payload: AdminWalletAdjustmentParams,
): Promise<AdminWalletAdjustmentResponseDTO> {
  return post<AdminWalletAdjustmentResponseDTO>(`/v1/admin/users/${userId}/wallet-adjustments`, {
    type: payload.type,
    amount_minor: payload.amountMinor,
    remark: payload.remark,
  });
}

export interface AdminUserWalletTransactionListParams {
  page: number;
  pageSize: number;
  type: AdminWalletTransactionType;
}

export function listAdminUserWalletTransactions(
  userId: number,
  params: AdminUserWalletTransactionListParams,
): Promise<AdminWalletTransactionListResponseDTO> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('page_size', String(params.pageSize));
  if (params.type) qs.set('type', params.type);
  return get<AdminWalletTransactionListResponseDTO>(`/v1/admin/users/${userId}/wallet-transactions?${qs.toString()}`);
}
